import { FieldNames } from 'app/models/fields/names';
import { Select } from 'app/components/FormFields/Select';
import { Currency } from 'app/components/FormFields/Currency';
import { CurrencyAllowZero } from 'app/components/FormFields/CurrencyAllowZero';
import { SHARED } from 'app/models/questions/shared';
import {
  HOME_EQUITY_LOAN_PURPOSE_OPTIONS,
  PROPERTY_TYPE_OPTIONS,
  PROPERTY_USAGE_OPTIONS,
} from 'app/models/options/options';
import { Question } from 'app/models/types';
import { RadioField } from 'app/components/FormFields/RadioField';
import { TextInput } from '../../components/FormFields/TextInput';
import { isHomeEquityLoanPurposeOther } from '../fields/conditionals';
import { State } from '../../components/FormFields/State';
import { ZipCode } from '../../components/FormFields/ZipCode';
import { HOME_EQUITY_INQUIRY_FINAL_PAGE_QUESTION } from 'app/models/sections/constants';
import { Confirmation } from 'app/components/Confirmation';

/**
 * Takes in a field name and returns the corresponding home equity
 * question
 *
 * @param {string} name Field name
 * @returns Question with matching field name
 */
export const homeEquityInquiryQuestionByName = (name: string): Question => {
  return HOME_EQUITY_INQUIRY_QUESTIONS.find((question) => {
    return question.name === name;
  }) as Question;
};

/**
 * Takes in a question id and returns the corresponding home equity
 * question
 *
 * @param {number} id Question id
 * @returns Question with matching id
 */
export const homeEquityInquiryQuestionById = (id: number): Question => {
  return HOME_EQUITY_INQUIRY_QUESTIONS.find((question) => {
    return question.id === id;
  }) as Question;
};

/**
 * Takes in an array of field names and returns an array of questions
 *
 * @param {string[]} [names=[]] Field names
 * @returns Questions matching field names
 */
export const homeEquityInquiryQuestionsByName = (names: string[] = []): Question[] => {
  const questions: any[] = [];
  for (const name of names) {
    questions.push(homeEquityInquiryQuestionByName(name));
  }
  return questions;
};

const HOME_EQUITY_INQUIRY_QUESTIONS: Question[] = [
  {
    id       : 1001,
    component: Currency,
    label    : 'Estimated Home Value',
    name     : FieldNames.homeValue,
  },
  {
    id       : 1002,
    component: CurrencyAllowZero,
    label    : 'Current Mortgage Balance',
    name     : FieldNames.mortgageBalance,
  },
  {
    id: 1003,
    ...SHARED[FieldNames.propertyType],
    component   : Select,
    options     : PROPERTY_TYPE_OPTIONS,
    fieldPopover: true,
  },
  {
    id: 1004,
    ...SHARED[FieldNames.propertyUsage],
    component   : Select,
    options     : PROPERTY_USAGE_OPTIONS,
    fieldPopover: true,
  },
  {
    id       : 1005,
    component: Currency,
    label    : 'Loan Amount',
    title    : 'How much would you like to borrow?',
    name     : FieldNames.loanAmount,
    popover  : 'You can\'t borrow more than the maximum loan amount shown on the previous page.',
  },
  {
    id: 1006,
    ...SHARED[FieldNames.firstName],
  },
  {
    id: 1007,
    ...SHARED[FieldNames.middleName],
  },
  {
    id: 1008,
    ...SHARED[FieldNames.lastName],
  },
  {
    id: 1009,
    ...SHARED[FieldNames.email],
  },
  {
    id: 1010,
    ...SHARED[FieldNames.phone],
  },
  {
    id: 1011,
    ...SHARED[FieldNames.suffixName],
  },
  {
    id       : 1012,
    component: RadioField,
    name     : FieldNames.homeEquityLoanPurpose,
    options  : HOME_EQUITY_LOAN_PURPOSE_OPTIONS,
    title    : 'What is the purpose of your home equity loan?',
  },
  {
    id       : 1013,
    component: TextInput,
    name     : FieldNames.homeEquityLoanPurposeOther,
    label    : 'Reason',
    showIf   : [isHomeEquityLoanPurposeOther],
  },
  {
    id       : 1014,
    component: TextInput,
    name     : FieldNames.propertyStreet,
    label    : 'Street Address',
    validator: {
      type : 'isMailingAddress',
      param: FieldNames.propertyStreet,
    },
  },
  {
    id        : 1015,
    component : TextInput,
    name      : FieldNames.propertyStreet2,
    label     : 'Street Address 2',
    isOptional: true,
    validator: {
      type : 'isMailingAddress',
      param: FieldNames.propertyStreet2,
    },
  },
  {
    id       : 1016,
    component: TextInput,
    name     : FieldNames.propertyCity,
    label    : 'City',
  },
  {
    id       : 1017,
    component: State,
    name     : FieldNames.propertyState,
    label    : 'State',
  },
  {
    id       : 1018,
    component: ZipCode,
    name     : FieldNames.propertyZip,
    label    : 'Zip',
  },
  {
    id: 1019,
    ...SHARED[FieldNames.dateOfBirth],
  },
  {
    id          : HOME_EQUITY_INQUIRY_FINAL_PAGE_QUESTION,
    component   : Confirmation,
    name        : 'FinalPage',
  },
];
